import { assembleSet, sha256Hex } from "./loader";
import { languageChain, parseArtifactEntry, parseLanguages } from "./manifest";
import type { CatalogSet, CatalogStorage, ClientIdentity, DigestProvider, VerifiedArtifact } from "./types";

// Last working catalog of a client build, for offline starts. One record per
// client and contract version, written with a single setItem, so a reader sees
// either the previous or the next complete set. The record holds public
// catalog text only: no account, profile or session values. Every read is
// re-verified (identity, structure, byte length, sha256); anything that fails
// is removed and treated as absent.

const RECORD_VERSION = 1;
const REVISION = /^[0-9a-f]{16}$/;

export function cacheKey(identity: ClientIdentity): string {
  return `voltie.l10n.catalog.v${RECORD_VERSION}:${identity.clientId}:c${identity.contractVersion}`;
}

interface CachedRecord {
  readonly v: number;
  readonly client_id: string;
  readonly contract_version: number;
  readonly contract_sha256: string;
  readonly embedded_revision: string;
  readonly revision: string;
  readonly language: string;
  readonly base_language: string;
  readonly languages: readonly string[];
  readonly fallback: Readonly<Record<string, readonly string[]>>;
  readonly artifacts: readonly { language: string; namespace: string; path: string; sha256: string; bytes: number; text: string }[];
}

export type CacheReadResult = { readonly set: CatalogSet } | { readonly set: null; readonly rejected: "absent" | "storage" | "corrupt" | "foreign" };

const encoder = new TextEncoder();

function toBuffer(text: string): ArrayBuffer {
  const bytes = encoder.encode(text);
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

async function decode(raw: string, identity: ClientIdentity, crypto: DigestProvider): Promise<CatalogSet | "foreign"> {
  const record = JSON.parse(raw) as CachedRecord;
  if (typeof record !== "object" || record === null || record.v !== RECORD_VERSION) throw new Error("record version");
  if (
    record.client_id !== identity.clientId ||
    record.contract_version !== identity.contractVersion ||
    record.contract_sha256 !== identity.contractSha256 ||
    record.embedded_revision !== identity.embeddedRevision
  ) {
    return "foreign";
  }
  if (typeof record.revision !== "string" || !REVISION.test(record.revision) || record.base_language !== identity.baseLanguage) throw new Error("revision");
  const { languages, fallback } = parseLanguages(record.languages, record.fallback, record.base_language);
  const chain = languageChain({ languages, fallback }, record.language);
  if (!Array.isArray(record.artifacts)) throw new Error("artifacts");

  const artifacts: VerifiedArtifact[] = [];
  const seen = new Set<string>();
  for (const a of record.artifacts) {
    if (!chain.includes(a.language) || !identity.namespaces.includes(a.namespace) || typeof a.text !== "string") throw new Error("artifact scope");
    const id = `${a.language}/${a.namespace}`;
    if (seen.has(id)) throw new Error("duplicate artifact");
    seen.add(id);
    const entry = parseArtifactEntry({ path: a.path, sha256: a.sha256, bytes: a.bytes }, a.language, a.namespace);
    const bytes = toBuffer(a.text);
    if (bytes.byteLength !== entry.bytes || (await sha256Hex(crypto, bytes)) !== entry.sha256) throw new Error("integrity");
    artifacts.push({ language: a.language, namespace: a.namespace, entry, text: a.text });
  }
  // The base language must be complete for the client, as in a fresh load.
  for (const ns of identity.namespaces) if (!seen.has(`${record.base_language}/${ns}`)) throw new Error("incomplete");
  return assembleSet({ catalog_revision: record.revision, base_language: record.base_language, languages, fallback }, record.language, artifacts);
}

/** Reads and re-verifies the cached set of this client build; never throws. */
export async function readCachedSet(storage: CatalogStorage | null, identity: ClientIdentity, crypto: DigestProvider): Promise<CacheReadResult> {
  if (!storage) return { set: null, rejected: "storage" };
  const key = cacheKey(identity);
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return { set: null, rejected: "storage" };
  }
  if (raw === null) return { set: null, rejected: "absent" };
  let result: CatalogSet | "foreign";
  try {
    result = await decode(raw, identity, crypto);
  } catch {
    removeQuietly(storage, key, raw);
    return { set: null, rejected: "corrupt" };
  }
  if (result === "foreign") {
    // Written by another build of this client: its contract or embedded catalog may differ.
    removeQuietly(storage, key, raw);
    return { set: null, rejected: "foreign" };
  }
  return { set: result };
}

/**
 * Removes the record that was read, and only that one: verification awaits
 * digests, so a newer request may have stored a valid set in the meantime.
 */
function removeQuietly(storage: CatalogStorage, key: string, rejected: string): void {
  try {
    if (storage.getItem(key) === rejected) storage.removeItem(key);
  } catch {
    // Storage that cannot remove cannot serve the entry either; nothing else to do.
  }
}

/** Stores a complete verified set as one record; returns false if storage refused it. */
export function writeCachedSet(storage: CatalogStorage | null, identity: ClientIdentity, set: CatalogSet): boolean {
  if (!storage) return false;
  const record: CachedRecord = {
    v: RECORD_VERSION,
    client_id: identity.clientId,
    contract_version: identity.contractVersion,
    contract_sha256: identity.contractSha256,
    embedded_revision: identity.embeddedRevision,
    revision: set.revision,
    language: set.language,
    base_language: set.baseLanguage,
    languages: set.languages,
    fallback: set.fallback,
    artifacts: set.artifacts.map((a) => ({ language: a.language, namespace: a.namespace, path: a.entry.path, sha256: a.entry.sha256, bytes: a.entry.bytes, text: a.text })),
  };
  try {
    storage.setItem(cacheKey(identity), JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}
